import apiClient from "@/app/api/client";
import { replaceParams, taskEndpoints } from "@/app/api/endpoints";
import type {
	AddTaskLocationsRequest,
	AddTaskPlantsRequest,
	CompleteTaskItemRequest,
	CreateTaskRequest,
	ListTasksParams,
	PaginatedResponse,
	Task,
	UpdateTaskRequest,
	UpdateTaskStatusRequest,
} from "../types";

export const taskService = {
	async list(params?: ListTasksParams): Promise<PaginatedResponse<Task>> {
		const response = await apiClient.get<PaginatedResponse<Task>>(taskEndpoints.list, {
			params,
		});
		return response.data;
	},

	async getById(id: string): Promise<Task> {
		const url = replaceParams(taskEndpoints.getById, { id });
		const response = await apiClient.get<{ success: true; data: Task }>(url);
		return response.data.data;
	},

	async create(data: CreateTaskRequest): Promise<Task> {
		const response = await apiClient.post<{ success: true; data: Task }>(
			taskEndpoints.create,
			data
		);
		return response.data.data;
	},

	async update(id: string, data: UpdateTaskRequest): Promise<Task> {
		const url = replaceParams(taskEndpoints.update, { id });
		const response = await apiClient.patch<{ success: true; data: Task }>(url, data);
		return response.data.data;
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(taskEndpoints.delete, { id });
		await apiClient.delete(url);
	},

	async updateStatus(id: string, data: UpdateTaskStatusRequest): Promise<unknown> {
		const url = replaceParams(taskEndpoints.updateStatus, { id });
		const response = await apiClient.patch<{ success: true; data: unknown }>(url, data);
		return response.data.data;
	},

	async addLocations(id: string, data: AddTaskLocationsRequest): Promise<Task> {
		const url = replaceParams(taskEndpoints.addLocations, { id });
		const response = await apiClient.post<{ success: true; data: Task }>(url, data);
		return response.data.data;
	},

	async removeLocation(id: string, locationId: string): Promise<Task> {
		const url = replaceParams(taskEndpoints.removeLocation, { id, locationId });
		const response = await apiClient.delete<{ success: true; data: Task }>(url);
		return response.data.data;
	},

	async addPlants(id: string, data: AddTaskPlantsRequest): Promise<Task> {
		const url = replaceParams(taskEndpoints.addPlants, { id });
		const response = await apiClient.post<{ success: true; data: Task }>(url, data);
		return response.data.data;
	},

	async removePlant(id: string, plantId: string): Promise<Task> {
		const url = replaceParams(taskEndpoints.removePlant, { id, plantId });
		const response = await apiClient.delete<{ success: true; data: Task }>(url);
		return response.data.data;
	},

	async completeLocation(
		id: string,
		locationId: string,
		data: CompleteTaskItemRequest
	): Promise<Task> {
		const url = replaceParams(taskEndpoints.completeLocation, { id, locationId });
		const response = await apiClient.post<{ success: true; data: Task }>(url, data);
		return response.data.data;
	},

	async completePlant(id: string, plantId: string, data: CompleteTaskItemRequest): Promise<Task> {
		const url = replaceParams(taskEndpoints.completePlant, { id, plantId });
		const response = await apiClient.post<{ success: true; data: Task }>(url, data);
		return response.data.data;
	},
};
